import { Loader2, Paperclip, X } from 'lucide-react';
import type { RefObject } from 'react';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import type { LiksChatComposerStagedAttachment } from './LiksChatComposer.types';
import './LiksChatComposerAttachmentsMenu.css';

export type LiksChatComposerAttachmentsMenuProps = {
  menuRef: RefObject<HTMLDivElement | null>;
  fileInputRef: RefObject<HTMLInputElement | null>;
  tooltip: string;
  menuLabel: string;
  emptyLabel: string;
  uploadLabel: string;
  removeLabel: string;
  uploadingLabel: string;
  uploadFailedLabel: string;
  stagedAttachments: LiksChatComposerStagedAttachment[];
  isOpen: boolean;
  isLocked: boolean;
  isControlsDisabled: boolean;
  onToggleOpen: () => void;
  onAddFiles: (fileList: FileList | null) => void;
  onRemove: (attachmentId: string) => void;
};

export function LiksChatComposerAttachmentsMenu({
  menuRef,
  fileInputRef,
  tooltip,
  menuLabel,
  emptyLabel,
  uploadLabel,
  removeLabel,
  uploadingLabel,
  uploadFailedLabel,
  stagedAttachments,
  isOpen,
  isLocked,
  isControlsDisabled,
  onToggleOpen,
  onAddFiles,
  onRemove,
}: LiksChatComposerAttachmentsMenuProps) {
  return (
    <div className="liks-chat-composer__menu-wrap" ref={menuRef}>
      <LiksTooltip content={tooltip} placement="top-start">
        <button
          type="button"
          className={[
            'liks-chat-composer__attach',
            stagedAttachments.length > 0 &&
              'liks-chat-composer__attach--active',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={tooltip}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={onToggleOpen}
          disabled={isControlsDisabled || isLocked}
        >
          <Paperclip
            className="liks-chat-composer__attach-icon"
            strokeWidth={2}
            aria-hidden
          />
          {stagedAttachments.length > 0 && (
            <span
              className="liks-chat-composer__linked-count"
              aria-label={`${stagedAttachments.length}`}
            >
              {stagedAttachments.length}
            </span>
          )}
        </button>
      </LiksTooltip>
      {isOpen && !isLocked && (
        <div
          className="liks-chat-composer__attachments-menu"
          role="dialog"
          aria-label={menuLabel}
        >
          <div className="liks-chat-composer__attachments-list liks-scrollbar">
            {stagedAttachments.length === 0 ? (
              <p className="liks-chat-composer__knowledge-empty">
                {emptyLabel}
              </p>
            ) : (
              stagedAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className={[
                    'liks-chat-composer__attachment-item',
                    attachment.status === 'error' &&
                      'liks-chat-composer__attachment-item--error',
                    attachment.status === 'uploading' &&
                      'liks-chat-composer__attachment-item--uploading',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="liks-chat-composer__attachment-main">
                    <div
                      className="liks-chat-composer__attachment-name-wrap"
                      aria-busy={attachment.status === 'uploading'}
                      aria-label={
                        attachment.status === 'uploading'
                          ? uploadingLabel
                          : undefined
                      }
                    >
                      {attachment.status === 'uploading' && (
                        <Loader2
                          className="liks-chat-composer__attachment-spinner"
                          strokeWidth={2}
                          aria-hidden
                        />
                      )}
                      <span className="liks-chat-composer__attachment-name">
                        {attachment.fileName}
                      </span>
                    </div>
                    {attachment.status === 'error' && (
                      <span className="liks-chat-composer__attachment-status liks-chat-composer__attachment-status--error">
                        {uploadFailedLabel}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="liks-chat-composer__attachment-remove"
                    aria-label={removeLabel}
                    onClick={() => onRemove(attachment.id)}
                    disabled={isControlsDisabled}
                  >
                    <X
                      className="liks-chat-composer__attachment-remove-icon"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </button>
                </div>
              ))
            )}
          </div>
          <button
            type="button"
            className="liks-chat-composer__upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isControlsDisabled || isLocked}
          >
            {uploadLabel}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="liks-chat-composer__file-input"
            multiple
            onChange={(event) => {
              onAddFiles(event.target.files);
              event.target.value = '';
            }}
          />
        </div>
      )}
    </div>
  );
}
